import React from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {IElementQuestionnaire, IElementQuestionnaireWithAnswers} from '../../../../incase-domain/elements/base/questionElementBase';
import {selectedPresentationMetaSelector} from '../../../../store/selectors/presentation.selectors';
import {getAttemptsAndRetriable} from '../../../../incase-domain/utils/retryCount';
import {ELEMENT} from '../../../../incase-domain/elements/base/ElementTypes';
import {AnswerTypes} from '../question/QuestionAnswer';

interface IQuestionCounterProps {
    element: IElementQuestionnaire;
}

function QuestionCounter(props: IQuestionCounterProps): JSX.Element {
    const {element} = props;
    const {t} = useTranslation();
    const presentationMeta = useSelector(selectedPresentationMetaSelector);
    const attemptsAndRetriable = getAttemptsAndRetriable(element, presentationMeta);

    const isElementYesNoQuestion = element.type === ELEMENT.YES_NO_QUESTION || element.type === ELEMENT.YES_NO_QUESTION_V2;
    const isElementLikertScale = element.type === ELEMENT.LIKERT_SCALE;

    const hideAttemptsCounter = !attemptsAndRetriable?.retriable || isElementYesNoQuestion || isElementLikertScale;

    function shouldShowSingleAttempt(): boolean {
        return !attemptsAndRetriable?.retriable;
    }

    function getLabel(): string {
        if (attemptsAndRetriable?.retriable && !isElementYesNoQuestion) {
            return `2 / ${attemptsAndRetriable.maxAttempts} ${t('editorPage.attempts')}`;
        }
        return t('editorPage.singleAttempt');
    }

    function getProgressBarWidth(): string {
        if (attemptsAndRetriable?.retriable && attemptsAndRetriable.maxAttempts && isElementYesNoQuestion) {
            return `${(2 / attemptsAndRetriable.maxAttempts) * 100}%`;
        }
        return '100%';
    }

    if (element.showCorrectAnswersCount) {
        const answers = 'answers' in element ? (element as IElementQuestionnaireWithAnswers).answers : undefined;
        const correctAnswersCount =
            answers?.reduce((prev, curr) => {
                const isCorrect = curr.type === AnswerTypes.CORRECT;
                if (isCorrect) {
                    return prev + 1;
                }

                return prev;
            }, 0) ?? 0;

        return (
            <div className="c-questionsAnsweredCount c-questionsAnsweredCount--twoLabels">
                <span className="c-questionsAnsweredCount__label">
                    {correctAnswersCount} {t('editorPage.correctAnswers', {count: correctAnswersCount})}
                </span>
            </div>
        );
    }

    if (hideAttemptsCounter) {
        return (
            <div className="c-questionsAnsweredCount">
                <div className="c-questionsAnsweredCount__count--singleAttempt" />
            </div>
        );
    }

    return (
        <div className="c-questionsAnsweredCount">
            <div className={`c-questionsAnsweredCount__count${shouldShowSingleAttempt() ? '--singleAttempt' : ''}`}>{getLabel()}</div>

            <div className="c-questionsAnsweredCount__progressBar">
                <div
                    className={`c-questionsAnsweredCount__progressBar_fill${shouldShowSingleAttempt() ? '--singleAttempt' : ''}`}
                    style={{width: getProgressBarWidth()}}
                />
            </div>
        </div>
    );
}

export default QuestionCounter;
